<template>
  <div class="landing">
    <header class="landing-nav">
      <div class="landing-container landing-nav__inner">
        <router-link to="/" class="landing-brand">
          <img src="/logo-banner.png" alt="SocialLabs" class="landing-brand__logo" />
        </router-link>

        <nav class="landing-nav__links" :class="{ 'is-open': navOpen }">
          <a href="#features" @click="navOpen = false">{{ $t('landing.nav_features') }}</a>
          <a href="#how" @click="navOpen = false">{{ $t('landing.nav_how') }}</a>
          <a href="#pricing" @click="navOpen = false">{{ $t('landing.nav_pricing') }}</a>
          <router-link to="/ai/studio/api" @click.native="navOpen = false">{{ $t('studioApi.api_docs') }}</router-link>
        </nav>

        <div class="landing-nav__actions">
          <LocaleSwitcher />
          <template v-if="isLoggedIn">
            <router-link to="/dashboard" class="btn btn-primary btn-sm">
              {{ $t('dashboard.title') }}
            </router-link>
          </template>
          <template v-else>
            <router-link to="/login" class="btn btn-outline-primary btn-sm d-none d-md-inline-flex">
              {{ $t('auth.sign_in') }}
            </router-link>
            <router-link to="/register" class="btn btn-primary btn-sm">
              {{ $t('landing.cta_start') }}
            </router-link>
          </template>
          <button type="button" class="landing-nav__toggle d-md-none" :aria-label="$t('landing.menu')" @click="navOpen = !navOpen">
            <i :class="navOpen ? 'fas fa-times' : 'fas fa-bars'"></i>
          </button>
        </div>
      </div>
    </header>

    <section class="landing-hero">
      <div class="landing-container">
        <div class="row align-items-center">
          <div class="col-lg-6 mb-4 mb-lg-0">
            <span class="landing-eyebrow">{{ $t('landing.hero_eyebrow') }}</span>
            <h1 class="landing-hero__title">{{ $t('landing.hero_title') }}</h1>
            <p class="landing-hero__lead">{{ $t('landing.hero_subtitle') }}</p>
            <div class="landing-hero__cta">
              <router-link v-if="isLoggedIn" to="/dashboard" class="btn btn-primary btn-lg">
                {{ $t('dashboard.title') }}
              </router-link>
              <template v-else>
                <router-link to="/register" class="btn btn-primary btn-lg">
                  {{ $t('landing.cta_start') }}
                </router-link>
                <router-link to="/login" class="btn btn-outline-primary btn-lg">
                  {{ $t('auth.sign_in') }}
                </router-link>
              </template>
            </div>
          </div>

          <div class="col-lg-6">
            <p class="landing-preview__label">{{ $t('landing.preview_illustration') }}</p>
            <div class="landing-preview" aria-hidden="true">
              <div class="row">
                <div v-for="stat in previewStats" :key="stat.key" class="col-6 mb-3">
                  <div class="dashboard-stat-card" :class="`dashboard-stat-card--${stat.tone}`">
                    <div class="dashboard-stat-card__icon"><i :class="stat.icon"></i></div>
                    <div class="dashboard-stat-card__body">
                      <div class="dashboard-stat-card__value">{{ stat.value }}</div>
                      <div class="dashboard-stat-card__label">{{ stat.label }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="landing-preview__card">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h5 class="mb-0">{{ $t('dashboard.this_week') }}</h5>
                    <small class="text-muted">{{ $t('landing.preview_week_hint') }}</small>
                  </div>
                  <span class="btn btn-sm btn-primary">
                    <i class="fas fa-plus mr-1"></i>{{ $t('dashboard.new_post') }}
                  </span>
                </div>
                <div class="week-grid">
                  <div
                    v-for="day in previewDays"
                    :key="day.key"
                    class="week-day"
                    :class="{ 'is-today': day.today, 'is-selected': day.selected }"
                  >
                    <div class="week-day__name">{{ day.name }}</div>
                    <div class="week-day__date">{{ day.date }}</div>
                    <div class="week-day__count">{{ day.count || '-' }}</div>
                    <div class="week-day__dots">
                      <span v-for="n in day.dots" :key="n" class="week-dot" :class="'tone-' + day.tone" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="features" class="landing-section">
      <div class="landing-container">
        <div class="landing-section__head landing-section__head--left">
          <h2>{{ $t('landing.features_title') }}</h2>
          <p>{{ $t('landing.features_subtitle') }}</p>
        </div>
        <div class="row mb-30">
          <div v-for="feature in flagshipFeatures" :key="feature.key" class="col-md-6 mb-30">
            <div class="landing-feature landing-feature--lead">
              <div class="dashboard-stat-card__icon" :class="`dashboard-stat-card--${feature.tone}`">
                <i :class="feature.icon"></i>
              </div>
              <h5>{{ feature.title }}</h5>
              <p>{{ feature.body }}</p>
            </div>
          </div>
        </div>
        <div class="row">
          <div
            v-for="feature in secondaryFeatures"
            :key="feature.key"
            :class="feature.colClass"
          >
            <div class="landing-feature landing-feature--compact">
              <div class="dashboard-stat-card__icon" :class="`dashboard-stat-card--${feature.tone}`">
                <i :class="feature.icon"></i>
              </div>
              <h5>{{ feature.title }}</h5>
              <p>{{ feature.body }}</p>
              <router-link v-if="feature.key === 'api'" to="/ai/studio/api" class="landing-feature__link">
                {{ $t('studioApi.api_docs') }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="how" class="landing-section landing-section--subtle">
      <div class="landing-container">
        <div class="row align-items-start">
          <div class="col-lg-4 mb-30 mb-lg-0">
            <h2 class="landing-section__title-left">{{ $t('landing.how_title') }}</h2>
            <p class="text-muted mb-0">{{ $t('landing.how_subtitle') }}</p>
          </div>
          <div class="col-lg-8">
            <ol class="landing-workflow list-unstyled mb-0">
              <li v-for="step in steps" :key="step.key" class="landing-workflow__step">
                <div class="landing-workflow__icon"><i :class="step.icon"></i></div>
                <div>
                  <h5 class="mb-1">{{ step.title }}</h5>
                  <p class="mb-0 text-muted">{{ step.body }}</p>
                </div>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </section>

    <section id="pricing" class="landing-section">
      <div class="landing-container">
        <div class="landing-section__head">
          <h2>{{ $t('landing.pricing_title') }}</h2>
          <p>{{ $t('landing.pricing_subtitle') }}</p>
        </div>
        <div class="row">
          <div v-for="plan in plans" :key="plan.slug" class="col-lg-4 col-md-6 mb-30 d-flex">
            <ContentCard :class="['pricing-card', { 'pricing-card--current': plan.slug === 'free' }]">
              <div class="pricing-header mb-3">
                <div class="d-flex align-items-center justify-content-between">
                  <h4 class="mb-1">{{ plan.name }}</h4>
                </div>
                <p class="text-muted small mb-0">{{ plan.description }}</p>
              </div>
              <div class="pricing-price mb-4">
                <span class="pricing-amount">{{ formatPrimaryPrice(plan) }}</span>
                <span v-if="planPrimaryAmount(plan) > 0" class="text-muted">{{ $t('billing.per_interval', { interval: plan.interval }) }}</span>
                <div v-if="secondaryPrice(plan)" class="text-muted small mt-1">
                  {{ $t('billing.or_per_month', { price: secondaryPrice(plan) }) }}
                </div>
              </div>
              <ul class="pricing-features list-unstyled mb-4">
                <li v-for="(label, idx) in featureList(plan)" :key="idx">
                  <i class="fas fa-check text-success mr-2"></i>{{ label }}
                </li>
              </ul>
              <router-link :to="isLoggedIn ? '/billing' : '/register'" class="btn btn-primary w-100 pricing-action">
                {{ isLoggedIn ? $t('landing.cta_choose') : (plan.slug === 'free' ? $t('landing.cta_start') : $t('landing.cta_choose')) }}
              </router-link>
            </ContentCard>
          </div>
        </div>
      </div>
    </section>

    <section class="landing-cta">
      <div class="landing-container text-center">
        <h2>{{ $t('landing.final_title') }}</h2>
        <p>{{ $t('landing.final_subtitle') }}</p>
        <router-link v-if="isLoggedIn" to="/dashboard" class="btn btn-light btn-lg">{{ $t('dashboard.title') }}</router-link>
        <router-link v-else to="/register" class="btn btn-light btn-lg">{{ $t('landing.cta_start') }}</router-link>
      </div>
    </section>

    <footer class="landing-footer">
      <div class="landing-container d-flex flex-wrap justify-content-between align-items-center">
        <div class="landing-footer__brand">
          <img src="/logo-banner.png" alt="SocialLabs" class="landing-footer__logo" />
          <span>{{ $t('apps.app_description') }}</span>
        </div>
        <div class="landing-footer__links">
          <router-link to="/ai/studio/api">{{ $t('studioApi.api_docs') }}</router-link>
          <router-link to="/terms">{{ $t('legal.terms') }}</router-link>
          <router-link to="/privacy">{{ $t('legal.privacy') }}</router-link>
          <router-link to="/cookies">{{ $t('legal.cookies') }}</router-link>
          <router-link to="/refund">{{ $t('legal.refund') }}</router-link>
          <template v-if="isLoggedIn">
            <router-link to="/dashboard">{{ $t('dashboard.title') }}</router-link>
          </template>
          <template v-else>
            <router-link to="/login">{{ $t('auth.sign_in') }}</router-link>
            <router-link to="/register">{{ $t('auth.register') }}</router-link>
          </template>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import LocaleSwitcher from '@/components/LocaleSwitcher.vue'
import { formatCurrency, planPrimaryAmount, planSecondaryAmount, planSecondaryCurrency } from '@/utils/currency'
import { planFeatureList } from '@/utils/planFeatures'

export default {
  components: { LocaleSwitcher },
  data() {
    return {
      navOpen: false,
    }
  },
  computed: {
    ...mapGetters('billing', ['plans']),
    ...mapGetters('app', ['locale', 'currency']),
    ...mapGetters('auth', ['isLoggedIn']),
    previewStats() {
      return [
        { key: 'studio', tone: 'primary', icon: 'fas fa-paint-brush', value: this.$t('landing.stat_studio_name'), label: this.$t('landing.stat_studio') },
        { key: 'posts', tone: 'success', icon: 'fas fa-calendar-alt', value: this.$t('landing.stat_schedule_name'), label: this.$t('landing.stat_schedule') },
        { key: 'ai', tone: 'info', icon: 'fas fa-comment-dots', value: this.$t('landing.stat_ai_name'), label: this.$t('landing.stat_ai') },
        { key: 'api', tone: 'warning', icon: 'fas fa-plug', value: this.$t('landing.stat_api_name'), label: this.$t('landing.stat_api') },
      ]
    },
    previewDays() {
      const names = [1, 2, 3, 4, 5, 6, 0].map((d) => this.$t(`common.days_short.${d}`))
      return [
        { key: 'm', name: names[0], date: 8, count: 2, dots: 2, tone: 'primary' },
        { key: 't', name: names[1], date: 9, count: 1, dots: 1, tone: 'success' },
        { key: 'w', name: names[2], date: 10, today: true, selected: true, count: 3, dots: 3, tone: 'primary' },
        { key: 'th', name: names[3], date: 11, count: '', dots: 0, tone: 'secondary' },
        { key: 'f', name: names[4], date: 12, count: 1, dots: 1, tone: 'success' },
        { key: 's', name: names[5], date: 13, count: '', dots: 0, tone: 'secondary' },
        { key: 'su', name: names[6], date: 14, count: 1, dots: 1, tone: 'info' },
      ]
    },
    features() {
      return [
        { key: 'studio', featured: true, tone: 'primary', icon: 'fas fa-paint-brush', title: this.$t('landing.feat_studio_title'), body: this.$t('landing.feat_studio_body') },
        { key: 'schedule', featured: true, tone: 'success', icon: 'fas fa-calendar-alt', title: this.$t('landing.feat_schedule_title'), body: this.$t('landing.feat_schedule_body') },
        { key: 'ai', tone: 'info', icon: 'fas fa-comment-dots', title: this.$t('landing.feat_ai_title'), body: this.$t('landing.feat_ai_body') },
        { key: 'analytics', tone: 'warning', icon: 'fas fa-chart-bar', title: this.$t('nav.analytics'), body: this.$t('landing.feat_analytics_body') },
        { key: 'storage', tone: 'primary', icon: 'fas fa-hdd', title: this.$t('nav.storage'), body: this.$t('landing.feat_storage_body') },
        { key: 'api', tone: 'info', icon: 'fas fa-plug', title: this.$t('nav.design_studio_api'), body: this.$t('landing.feat_api_body') },
      ]
    },
    flagshipFeatures() {
      return this.features.filter((f) => f.featured)
    },
    secondaryFeatures() {
      const cols = {
        ai: 'col-lg-3 col-md-6 mb-30',
        analytics: 'col-lg-3 col-md-6 mb-30',
        storage: 'col-lg-3 col-md-6 mb-30',
        api: 'col-lg-3 col-md-6 mb-30',
      }
      return this.features
        .filter((f) => !f.featured)
        .map((f) => ({ ...f, colClass: cols[f.key] || 'col-lg-3 col-md-6 mb-30' }))
    },
    steps() {
      return [
        { key: 'design', icon: 'fas fa-paint-brush', title: this.$t('landing.step1_title'), body: this.$t('landing.step1_body') },
        { key: 'schedule', icon: 'fas fa-check-double', title: this.$t('landing.step2_title'), body: this.$t('landing.step2_body') },
        { key: 'publish', icon: 'fab fa-instagram', title: this.$t('landing.step3_title'), body: this.$t('landing.step3_body') },
      ]
    },
  },
  created() {
    this.fetchPlans()
  },
  mounted() {
    document.documentElement.style.scrollBehavior = 'smooth'
  },
  beforeDestroy() {
    document.documentElement.style.scrollBehavior = ''
  },
  methods: {
    ...mapActions('billing', ['fetchPlans']),
    planPrimaryAmount(plan) {
      return planPrimaryAmount(plan, this.currency)
    },
    formatPrimaryPrice(plan) {
      const amount = this.planPrimaryAmount(plan)
      if (!amount) return this.$t('billing.free_price')
      return formatCurrency(amount, this.currency, this.locale)
    },
    secondaryPrice(plan) {
      const amount = planSecondaryAmount(plan, this.currency)
      if (!amount) return ''
      return formatCurrency(amount, planSecondaryCurrency(this.currency), this.locale)
    },
    featureList(plan) {
      const flags = {
        hide_ai: this.$store.getters['app/hideAI'],
        hide_scheduler: this.$store.getters['app/hideScheduler'],
      }
      return planFeatureList(plan, this.$t.bind(this), flags)
    },
  },
}
</script>

<style scoped>
.landing {
  min-height: 100vh;
  background: var(--wc-bg-page);
  color: var(--wc-text);
  overflow-x: hidden;
}

.landing-container {
  width: 100%;
  max-width: 1140px;
  margin: 0 auto;
  padding: 0 var(--wc-space-20);
}

.landing-nav {
  position: sticky;
  top: 0;
  z-index: 40;
  background: var(--wc-bg-card);
  border-bottom: 1px solid var(--wc-border);
  box-shadow: var(--wc-shadow-sm);
}

.landing-preview .dashboard-stat-card__value {
  font-size: 1.05rem;
}

.landing-nav__inner {
  display: flex;
  align-items: center;
  gap: 1rem;
  min-height: 68px;
}

.landing-brand {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  color: var(--wc-text-heading);
  font-weight: 700;
  font-size: 1.05rem;
  text-decoration: none;
  flex-shrink: 0;
}

.landing-brand:hover {
  color: var(--wc-primary);
  text-decoration: none;
}

.landing-brand__logo {
  height: 40px;
  width: auto;
  max-width: 220px;
  object-fit: contain;
  object-position: left center;
  display: block;
  flex-shrink: 0;
}

.landing-nav__links {
  display: none;
  align-items: center;
  gap: 1.25rem;
  margin-left: 0.5rem;
  flex: 1;
}

.landing-nav__links a {
  color: var(--wc-text);
  font-weight: 500;
  text-decoration: none;
}

.landing-nav__links a:hover {
  color: var(--wc-text-heading);
  text-decoration: none;
}

.landing-nav__actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-shrink: 0;
}

.landing-nav__actions .btn {
  display: inline-flex;
  align-items: center;
  height: 36px;
  padding: 0 0.9rem;
  line-height: 1;
  white-space: nowrap;
}

.landing-nav__toggle {
  border: 1px solid var(--wc-border);
  background: #fff;
  width: 36px;
  height: 36px;
  border-radius: var(--wc-radius-md);
  color: var(--wc-text-heading);
}

.landing-hero {
  padding: 3.5rem 0 3rem;
  background: linear-gradient(160deg, var(--wc-primary-light) 0%, rgba(var(--wc-primary-rgb), 0.14) 45%, var(--wc-bg-page) 100%);
}

.landing-eyebrow {
  display: inline-block;
  background: var(--wc-primary-light);
  color: var(--wc-primary);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  margin-bottom: 1rem;
}

.landing-hero__title {
  font-size: 2.35rem;
  font-weight: 700;
  color: var(--wc-text-heading);
  line-height: 1.25;
  margin-bottom: 1rem;
}

.landing-hero__lead {
  font-size: 1.05rem;
  color: var(--wc-text-muted);
  max-width: 34rem;
  margin-bottom: 1.5rem;
}

.landing-hero__cta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.landing-hero__cta .btn {
  display: inline-flex;
  align-items: center;
}

.landing-preview__label {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--wc-text-muted);
  margin-bottom: 0.65rem;
}

.landing-preview {
  background: transparent;
}

.landing-preview__card {
  background: var(--wc-bg-card);
  border: 1px solid var(--wc-border);
  border-radius: var(--wc-radius-xl);
  box-shadow: var(--wc-shadow-lg);
  padding: 1.25rem;
}

.landing-section {
  padding: 4rem 0 2.5rem;
  scroll-margin-top: 80px;
}

.landing-section--subtle {
  background: #fff;
  border-top: 1px solid var(--wc-border);
  border-bottom: 1px solid var(--wc-border);
}

.landing-section__head {
  text-align: center;
  max-width: 640px;
  margin: 0 auto 2.25rem;
}

.landing-section__head h2 {
  color: var(--wc-text-heading);
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.landing-section__head p {
  margin: 0;
  color: var(--wc-text-muted);
}

.landing-section__head--left {
  text-align: left;
  max-width: 720px;
  margin-left: 0;
  margin-right: 0;
  margin-bottom: 2rem;
}

.landing-section__title-left {
  color: var(--wc-text-heading);
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.landing-feature--lead {
  padding: 1.75rem;
}

.landing-feature--compact h5 {
  font-size: 1rem;
}

.landing-workflow__step {
  display: flex;
  gap: 1rem;
  padding: 1.25rem 0;
  border-bottom: 1px solid var(--wc-border);
}

.landing-workflow__step:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.landing-workflow__icon {
  width: 44px;
  height: 44px;
  border-radius: var(--wc-radius-md);
  background: var(--wc-primary-light);
  color: var(--wc-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1rem;
}

.landing-feature {
  height: 100%;
  background: var(--wc-bg-card);
  border: 1px solid var(--wc-border);
  border-radius: var(--wc-radius-xl);
  box-shadow: var(--wc-shadow-sm);
  padding: 1.5rem;
}

.landing-feature h5 {
  margin: 1rem 0 0.5rem;
  color: var(--wc-text-heading);
}

.landing-feature p {
  margin: 0;
  color: var(--wc-text-muted);
  font-size: 0.9rem;
}

.landing-feature__link {
  display: inline-block;
  margin-top: 0.75rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--wc-primary);
  text-decoration: none;
}

.landing-feature__link:hover {
  text-decoration: underline;
}

.landing-feature .dashboard-stat-card__icon {
  background: rgba(var(--wc-primary-rgb), 0.12);
  color: var(--wc-primary);
}

.landing-feature .dashboard-stat-card--success {
  background: rgba(34, 198, 171, 0.12);
  color: var(--wc-success);
}

.landing-feature .dashboard-stat-card--info {
  background: rgba(71, 152, 232, 0.12);
  color: var(--wc-info);
}

.landing-feature .dashboard-stat-card--warning {
  background: rgba(255, 188, 52, 0.15);
  color: #d48806;
}

.pricing-card {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.pricing-action {
  margin-top: auto;
}

.pricing-card--current {
  border: 2px solid var(--wc-primary);
}

.pricing-amount {
  font-size: 2rem;
  font-weight: 700;
  color: var(--wc-text-heading);
  margin-right: 0.25rem;
}

.pricing-features li {
  padding: 0.35rem 0;
  font-size: 0.9rem;
}

.landing-cta {
  padding: 4rem 0;
  background: var(--wc-primary);
  color: #fff;
}

.landing-cta h2 {
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.landing-cta p {
  color: #fff;
  margin-bottom: 1.5rem;
}

.landing-footer {
  padding: 1.25rem 0;
  background: var(--wc-bg-card);
  border-top: 1px solid var(--wc-border);
  font-size: 0.85rem;
  color: var(--wc-text-muted);
}

.landing-footer__brand {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
}

.landing-footer__logo {
  height: 36px;
  width: auto;
  max-width: 200px;
  object-fit: contain;
  object-position: left center;
  display: block;
  flex-shrink: 0;
}

.landing-footer__links {
  display: flex;
  gap: 1rem;
}

.landing-footer__links a {
  color: var(--wc-text);
  font-weight: 500;
}

.landing-footer__links a:hover {
  color: var(--wc-primary);
  text-decoration: none;
}

.week-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.5rem;
}

.week-day {
  border: 1px solid #e8ecef;
  background: #fff;
  border-radius: 12px;
  padding: 0.75rem 0.4rem;
  text-align: center;
  min-height: 96px;
}

.week-day.is-today { background: #f3fffb; }
.week-day.is-selected {
  border-color: var(--wc-primary);
  box-shadow: 0 0 0 2px rgba(var(--wc-primary-rgb), 0.18);
  background: #f0fffa;
}

.week-day__name {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--wc-text-muted);
  font-weight: 600;
}

.week-day__date {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2c3340;
  line-height: 1.3;
}

.week-day__count {
  font-size: 0.75rem;
  color: var(--wc-primary);
  font-weight: 600;
  min-height: 1.1em;
}

.week-day__dots {
  margin-top: 0.35rem;
  min-height: 8px;
  display: flex;
  justify-content: center;
  gap: 3px;
}

.week-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #adb5bd;
  display: inline-block;
}

.week-dot.tone-primary { background: #4798e8; }
.week-dot.tone-success { background: #22c6ab; }
.week-dot.tone-info { background: #17a2b8; }

@media (min-width: 768px) {
  .landing-nav__links {
    display: flex;
  }
}

@media (max-width: 767px) {
  .landing-hero {
    padding: 2rem 0 2.25rem;
  }

  .landing-hero__title {
    font-size: 1.75rem;
  }

  .landing-nav__links.is-open {
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 68px;
    left: 0;
    right: 0;
    background: #fff;
    padding: 1rem var(--wc-space-20) 1.25rem;
    border-bottom: 1px solid var(--wc-border);
    box-shadow: var(--wc-shadow);
  }

  .week-day {
    padding: 0.45rem 0.15rem;
    min-height: 84px;
  }

  .week-day__date {
    font-size: 1rem;
  }
}
</style>
